import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Mail, ArrowRight, Loader2, AlertTriangle, CheckCircle2, Copy, Database, Smartphone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_STORAGE_KEY = 'cp_admin_token';

export default function AdminDeviceRecovery() {
  const [token] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [email, setEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupError, setLookupError] = useState('');

  // Migration form
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [migrateBusy, setMigrateBusy] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [dryRun, setDryRun] = useState(true);

  // Live data summary for both device_ids.
  const [summaryFrom, setSummaryFrom] = useState(null);
  const [summaryTo, setSummaryTo] = useState(null);
  const [summaryBusy, setSummaryBusy] = useState(false);

  const headers = { 'X-Admin-Token': token };

  async function lookup() {
    setLookupError('');
    setLookupResult(null);
    const e = email.trim().toLowerCase();
    if (!e || !e.includes('@')) {
      setLookupError('Email invalide.');
      return;
    }
    setLookupLoading(true);
    try {
      const r = await axios.get(`${API}/stripe/admin/lookup-by-email?email=${encodeURIComponent(e)}`, { headers });
      setLookupResult(r.data || null);
    } catch (err) {
      setLookupError(err.response?.data?.error || err.message || 'Erreur réseau');
    } finally {
      setLookupLoading(false);
    }
  }

  const fetchSummary = useCallback(async (deviceId, setter) => {
    if (!deviceId.trim()) { setter(null); return; }
    try {
      const r = await axios.get(`${API}/admin/device-data-summary?device_id=${encodeURIComponent(deviceId.trim())}`, { headers });
      setter(r.data);
    } catch (_) { setter(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setSummaryBusy(true);
    Promise.all([fetchSummary(fromId, setSummaryFrom), fetchSummary(toId, setSummaryTo)])
      .finally(() => setSummaryBusy(false));
  }, [fromId, toId, fetchSummary]);

  async function migrate() {
    setMigrationResult(null);
    if (!fromId.trim() || !toId.trim()) return;
    if (fromId.trim() === toId.trim()) {
      setMigrationResult({ error: 'Les deux identifiants sont identiques.' });
      return;
    }
    if (!dryRun) {
      if (!window.confirm(`Migrer DÉFINITIVEMENT toutes les données de\n${fromId.trim()}\nvers\n${toId.trim()} ?\n\nCette action est irréversible.`)) return;
    }
    setMigrateBusy(true);
    try {
      const r = await axios.post(`${API}/admin/migrate-device-id`, {
        from_device_id: fromId.trim(),
        to_device_id: toId.trim(),
        dry_run: dryRun,
      }, { headers });
      setMigrationResult(r.data);
      if (!dryRun) {
        // Refresh both summaries
        await Promise.all([fetchSummary(fromId, setSummaryFrom), fetchSummary(toId, setSummaryTo)]);
      }
    } catch (err) {
      setMigrationResult({ error: err.response?.data?.error || err.message });
    } finally {
      setMigrateBusy(false);
    }
  }

  function copyText(t) {
    try { navigator.clipboard.writeText(t); } catch (_) {}
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center font-['Manrope']">
        <div className="text-center">
          <p className="text-[#888] mb-3">Authentification admin requise.</p>
          <Link to="/admin/country-events" className="text-[#FFC107] hover:underline" data-testid="goto-admin-login">
            Aller à l'écran de connexion →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-['Manrope']">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0D0D0D]/80 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/country-events" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4" /> Modération
            </Link>
            <span className="text-white/20">·</span>
            <span className="text-sm text-[#FFC107] font-bold">Récupération device_id</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-24 space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black mb-1">Récupération de données device_id</h1>
          <p className="text-sm text-[#A0A0A0]">
            Outil interne. Permet de retrouver l'ancien <code className="px-1 py-0.5 rounded bg-white/10 text-[#FFC107] text-xs">device_id</code> d'un utilisateur Pro depuis son email Stripe, puis de migrer ses données vers son nouveau <code className="px-1 py-0.5 rounded bg-white/10 text-[#FFC107] text-xs">device_id</code> après réinstallation.
          </p>
        </div>

        {/* Étape 1: Lookup */}
        <section className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-6">
          <h2 className="text-base font-bold mb-1 inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FFC107] text-black text-xs font-black">1</span>
            Retrouver l'ancien device_id par email Stripe
          </h2>
          <p className="text-xs text-[#888] ml-9 mb-4">Cherche dans <code>payment_transactions</code> + sessions Stripe Checkout du client.</p>
          <div className="flex flex-col sm:flex-row gap-2 ml-9">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                placeholder="user@example.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFC107] outline-none text-sm"
                data-testid="lookup-email-input"
              />
            </div>
            <button
              onClick={lookup}
              disabled={lookupLoading}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#FFC107] to-[#FF9800] text-black font-bold text-sm disabled:opacity-60"
              data-testid="lookup-btn"
            >
              {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Rechercher
            </button>
          </div>

          {lookupError && (
            <div className="mt-4 ml-9 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2" data-testid="lookup-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              {lookupError}
            </div>
          )}

          {lookupResult && (
            <div className="mt-5 ml-9 space-y-3" data-testid="lookup-results">
              {(() => {
                // Backend now returns `all_device_ids` consolidated from
                // payment_transactions + device_trials + Stripe metadata.
                const devices = Array.isArray(lookupResult.all_device_ids) && lookupResult.all_device_ids.length > 0
                  ? lookupResult.all_device_ids
                  : Array.from(new Set((lookupResult.transactions || []).map((t) => t.device_id).filter(Boolean)));
                const customers = lookupResult.stripe_customers || [];
                const trials = lookupResult.device_trials || [];
                if (devices.length === 0) {
                  return (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs space-y-2">
                      <p><b>Aucun device_id trouvé</b> dans aucune source pour cet email.</p>
                      {customers.length > 0 && (
                        <p>Stripe trouve bien {customers.length} client(s) ({customers.map((c) => c.id).join(', ')}) mais aucun device_id n'est attaché aux paiements / abonnements. Demandez à l'utilisateur d'aller sur <code>/whoami.html</code> sur son ancien install (s'il l'a encore) ou utilisez l'étape 2 manuellement si vous avez le device_id par un autre canal.</p>
                      )}
                      {trials.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer">Fallback : {trials.length} device_trials trouvé(s) par email/customer (cliquer)</summary>
                          <div className="mt-2 space-y-1">
                            {trials.map((t) => (
                              <div key={t.device_id} className="font-mono text-[11px] text-[#FFC107]">{t.device_id}</div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                }
                return (
                  <>
                    <p className="text-xs text-[#A0A0A0]">{devices.length} device_id trouvé{devices.length > 1 ? 's' : ''} pour <b>{lookupResult.email}</b> :</p>
                    {devices.map((d) => (
                      <div key={d} className="flex items-center gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid={`device-result-${d}`}>
                        <Database className="w-4 h-4 text-[#FFC107] shrink-0" />
                        <code className="flex-1 text-xs text-[#FFC107] break-all">{d}</code>
                        <button onClick={() => copyText(d)} className="p-1.5 rounded text-white/60 hover:text-white hover:bg-white/10" title="Copier" data-testid={`copy-device-${d}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setFromId(d)}
                          className="px-3 py-1 rounded-lg bg-[#FFC107]/15 hover:bg-[#FFC107]/25 text-[#FFC107] text-xs font-bold"
                          data-testid={`use-as-from-${d}`}
                        >
                          Utiliser comme ANCIEN
                        </button>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
        </section>

        {/* Étape 2: Migrate */}
        <section className="rounded-2xl bg-gradient-to-br from-[#141414] to-[#0B0B0B] border border-white/5 p-6">
          <h2 className="text-base font-bold mb-1 inline-flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#FFC107] text-black text-xs font-black">2</span>
            Migrer vers le nouveau device_id
          </h2>
          <p className="text-xs text-[#888] ml-9 mb-5">
            L'utilisateur doit ouvrir <code className="px-1 rounded bg-white/10 text-[#FFC107]">claypulsepro.com/whoami.html</code> sur son nouvel install et vous communiquer le device_id affiché.
          </p>

          <div className="ml-9 grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-start">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-red-300 font-bold mb-1.5">ANCIEN device_id</label>
              <input
                value={fromId}
                onChange={(e) => setFromId(e.target.value)}
                placeholder="cp_xxx_yyy"
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-red-500/30 focus:border-red-500 outline-none text-xs font-mono"
                data-testid="migrate-from-input"
              />
              <DeviceSummary summary={summaryFrom} kind="from" busy={summaryBusy} />
            </div>

            <div className="flex justify-center pt-7">
              <ArrowRight className="w-5 h-5 text-[#FFC107]" />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-green-300 font-bold mb-1.5">NOUVEAU device_id</label>
              <input
                value={toId}
                onChange={(e) => setToId(e.target.value)}
                placeholder="cp_xxx_yyy"
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-green-500/30 focus:border-green-500 outline-none text-xs font-mono"
                data-testid="migrate-to-input"
              />
              <DeviceSummary summary={summaryTo} kind="to" busy={summaryBusy} />
            </div>
          </div>

          <div className="ml-9 mt-5 flex items-center flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-[#C8C8C8] cursor-pointer">
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                className="w-4 h-4 accent-[#FFC107]"
                data-testid="dry-run-checkbox"
              />
              <span>Simulation (dry-run, n'écrit rien)</span>
            </label>
            <button
              onClick={migrate}
              disabled={migrateBusy || !fromId.trim() || !toId.trim()}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm disabled:opacity-50 ${dryRun ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-gradient-to-r from-[#FF6B35] to-[#FF9800] text-black'}`}
              data-testid="migrate-btn"
            >
              {migrateBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {dryRun ? 'Simuler la migration' : 'Migrer définitivement'}
            </button>
          </div>

          {migrationResult && (
            <div className="ml-9 mt-5" data-testid="migration-result">
              {migrationResult.error ? (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {migrationResult.error}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-green-200">
                  <div className="flex items-center gap-2 mb-2 font-bold">
                    <CheckCircle2 className="w-4 h-4" />
                    {migrationResult.dry_run
                      ? `Simulation OK — ${Object.values(migrationResult.by_collection || {}).reduce((s, v) => s + (v.would_move || 0), 0)} document(s) prêt(s) à être migré(s).`
                      : `Migration terminée — ${migrationResult.total_moved} document(s) migré(s).`}
                  </div>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-green-300 mb-2">Détail par collection</summary>
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-green-300/70 text-[10px] uppercase">
                          <th className="py-1 pr-3">Collection</th>
                          <th className="py-1 pr-3">{migrationResult.dry_run ? 'À migrer' : 'Migrés'}</th>
                          <th className="py-1">Déjà chez NEW</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono text-[11px]">
                        {Object.entries(migrationResult.by_collection || {}).map(([k, v]) => (
                          <tr key={k} className="border-t border-green-500/10">
                            <td className="py-1 pr-3">{k}</td>
                            <td className="py-1 pr-3 text-[#FFC107]">{migrationResult.dry_run ? v.would_move : v.moved}</td>
                            <td className="py-1 text-[#888]">{v.already_at_dst}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="rounded-2xl bg-yellow-500/5 border border-yellow-500/20 p-5">
          <h3 className="text-sm font-bold text-yellow-300 mb-2 inline-flex items-center gap-2">
            <Smartphone className="w-4 h-4" /> Procédure utilisateur recommandée
          </h3>
          <ol className="text-xs text-yellow-100/90 space-y-1.5 list-decimal pl-5">
            <li>L'utilisateur ouvre Safari et va sur <code className="px-1 rounded bg-black/40">claypulsepro.com/reset.html</code>.</li>
            <li>Niveau 3 (Hard reset) → tout est purgé.</li>
            <li>Il réinstalle la PWA depuis Safari → un nouveau device_id est généré.</li>
            <li>Il ouvre <code className="px-1 rounded bg-black/40">claypulsepro.com/whoami.html</code> et vous envoie le nouveau device_id.</li>
            <li>Vous utilisez cette page : étape 1 (email Stripe → ANCIEN), étape 2 (coller le NOUVEAU + migrer).</li>
          </ol>
        </section>
      </main>
    </div>
  );
}

function DeviceSummary({ summary, kind, busy }) {
  if (!summary && !busy) return null;
  const cls = kind === 'from' ? 'text-red-300/80' : 'text-green-300/80';
  if (!summary) {
    return <p className={`mt-2 text-[10px] ${cls}`}>Vérification…</p>;
  }
  if (summary.total === 0) {
    return <p className={`mt-2 text-[10px] ${cls}`}>Aucune donnée trouvée pour ce device_id.</p>;
  }
  return (
    <p className={`mt-2 text-[10px] ${cls}`} data-testid={`summary-${kind}`}>
      <b>{summary.total}</b> document(s) au total · {Object.entries(summary.counts).filter(([, v]) => v > 0).map(([k, v]) => `${v}× ${k.replace('cp_', '')}`).join(', ')}
    </p>
  );
}
